import { useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { IconButton, TableCell, TableRow } from "@mui/material";

import { roundToPrecision } from "../../../../../utils/numbers";
import { useCluster } from "../../../hooks";
import { type Container, type DashboardWorkload } from "../../../types";
import { getFormattedBillingCost } from "../../../utils";
import { TimeRange } from "../Components/TimeRangeSelector";
import { useRowHighlighting } from "./hooks";

const isSummaryContainer = (c: Container) => c.name === "all";

interface WorkloadsTableRowProps {
  row: DashboardWorkload;
  timeRange?: TimeRange;
  clusterId?: string;
}

const WorkloadsTableRow = ({ row, timeRange, clusterId }: WorkloadsTableRowProps) => {
  const rowHighlightingProps = useRowHighlighting();

  const [isCollapsed, setIsCollapsed] = useState(true);

  const containers: Container[] = row.containers?.filter((c) => !isSummaryContainer(c)) ?? [];
  const workloadContainersSummary = row.containers?.find(isSummaryContainer) ?? {
    cpuRequest: 0,
    cpuLimit: 0,
    memRequest: 0,
    memLimit: 0,
    name: "all",
  };

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const [cluster] = useCluster(clusterId);
  const doitBillingCosts = getFormattedBillingCost(cluster);

  const selectedTimeRange = timeRange ?? TimeRange.T_30D;
  const doitBillingCost = doitBillingCosts[selectedTimeRange]?.cost ?? 0;
  const showCostColumn = !["1h", "4h", "12h"].includes(timeRange ?? "");

  const renderResourceMetricCells = (container: Container) => (
    <>
      <TableCell align="right">{roundToPrecision(container.cpuRequest, 4)}</TableCell>
      <TableCell align="right">
        {container.cpuLimit === 0 ? "No Limit" : roundToPrecision(container.cpuLimit, 4)}
      </TableCell>
      <TableCell align="right">{roundToPrecision(container.memRequest, 1)} Mi</TableCell>
      <TableCell align="right">
        {container.memLimit === 0 ? "No Limit" : `${roundToPrecision(container.memLimit, 1)} Mi`}
      </TableCell>
    </>
  );

  return (
    <>
      <TableRow {...rowHighlightingProps}>
        <TableCell>
          <IconButton onClick={handleToggle} size="small">
            {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {row.name} ({containers.length})
        </TableCell>
        <TableCell>{row.type}</TableCell>
        <TableCell>{row.namespace}</TableCell>
        {/* Calculate the workloadcost based on DoiT Billing Cost proportionally */}
        {showCostColumn && (
          <TableCell align="right">
            ${Math.ceil((row.totalCost / row.clusterCost) * doitBillingCost * 1000) / 1000}
          </TableCell>
        )}
        <TableCell>All</TableCell>
        {renderResourceMetricCells(workloadContainersSummary)}
      </TableRow>
      {!isCollapsed &&
        containers
          .filter((c) => !isSummaryContainer(c))
          .map((container: Container) => (
            <TableRow {...rowHighlightingProps} key={container.name}>
              <TableCell sx={{ height: 47 }} /> {/* Same height as the collapse icon */}
              <TableCell />
              <TableCell />
              <TableCell />
              {showCostColumn && <TableCell />}
              <TableCell>{container.name}</TableCell>
              {renderResourceMetricCells(container)}
            </TableRow>
          ))}
    </>
  );
};

export default WorkloadsTableRow;
